import React, { useEffect, useState } from 'react';
import {
  Layout, Row, Col, Button, Tabs, Modal, Upload, message,
  notification, Space, Spin, Card, Typography, Statistic
} from 'antd';
import {
  UploadOutlined, SyncOutlined, CommentOutlined, WechatOutlined,
  ExclamationCircleFilled, DoubleRightOutlined, FileAddOutlined,
  FileTextOutlined, TeamOutlined, CheckCircleOutlined, ClockCircleOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import {
  gviewDashboardSelector, clearState as clearDashboardState, uploadPdfAsyn,
  fetchAllUserData, clearRoomUserData, getCollaborationNotification, updateNotification
} from './ChdDashboardSlice';
import { 
  getAllProcess, 
  getAllCollaborateDoc, getAllKiv,
  chdAllProcessSelector, 
  clearState as clearProcessState 
} from '../../Process/ChdAllProcessSlice';
import { authSelector } from '../../../Auth/authSlice';
import { useHistory } from "react-router-dom";

const { Content } = Layout;
const { Dragger } = Upload;
const { TabPane } = Tabs;
const { confirm } = Modal;
const { Title, Paragraph, Text } = Typography;
const antIcon = <SyncOutlined style={{ fontSize: 32, color: '#ff4500' }} spin />;

export default function Dashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector(authSelector);

  // Pulling Real Counts from ChdAllProcessSlice
  const { 
    processCount, 
    kivCount, 
    collaborateCount,
    isFetching: isProcessFetching 
  } = useSelector(chdAllProcessSelector);

  const {
    isError, isSuccess, errorMessage,
    NotificationData, isNotification
  } = useSelector(gviewDashboardSelector);

  const [isLoading, setLoading] = useState(false);
  const [isToast, setToast] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // --- UI Styles ---
  const styles = {
    container: {
      padding: '30px',
      background: '#f8f9fa',
      minHeight: '100vh',
 
      transition: 'margin-left 0.2s ease',
    },
    mainCard: {
      borderRadius: '16px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
      border: 'none',
      overflow: 'hidden'
    },
    welcomeSection: {
      background: 'linear-gradient(90deg, #172B4D 0%, #2D3E50 100%)',
      padding: '32px',
      borderRadius: '12px',
      marginBottom: '32px',
      color: '#fff'
    },
    kpiCard: (color) => ({
      borderRadius: '12px',
      border: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
      background: '#fff',
      borderLeft: `6px solid ${color}`,
      height: '100%'
    }),
    actionBtn: {
      height: '45px',
      borderRadius: '8px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    instructionBox: {
      padding: '24px',
      background: '#ffffff',
      border: '1px solid #f0f0f0',
      borderRadius: '12px',
      marginTop: '16px'
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    dispatch(getAllKiv(1));
    dispatch(clearDashboardState());
    dispatch(clearProcessState());

    // Fetch actual data to populate counts
    dispatch(getAllProcess(1)); 
    dispatch(getAllCollaborateDoc('')); 
    dispatch(getCollaborationNotification(user.userType));
    
    setTimeout(() => setLoading(false), 800);
  }, [dispatch, user.userType]);

  useEffect(() => {
    if (isError) {
      setLoading(false);
      setToast(true);
      toast.error(errorMessage);
      dispatch(clearDashboardState());
    }
    if (isSuccess) setLoading(false);
    
    if (isNotification && NotificationData?.data?.[0]?.receiver === user.username) {
      const notificationItem = NotificationData.data[0];
      notification.open({
        message: "New Collaboration Message",
        description: `${notificationItem.message}. TITLE: ${notificationItem.docName}`,
        icon: <WechatOutlined style={{ color: '#ff4500' }} />,
        duration: 0,
        btn: (
          <Button
            type="primary"
            size="small"
            style={{ backgroundColor: '#ff4500', borderColor: '#ff4500' }}
            icon={<CommentOutlined />}
            onClick={() => {
              dispatch(updateNotification({ roomId: notificationItem.roomId }, user.userType));
              dispatch(getCollaborationNotification(user.userType));
              history.push({
                pathname: '/new-document',
                state: { roomId: notificationItem.roomId }
              });
            }}
          >
            View
          </Button>
        )
      });
    }
  }, [isError, isSuccess, isNotification, NotificationData, dispatch, errorMessage, history, user.username, user.userType]);

  // FIXED: Defining handleUploadChange to avoid reference errors
  const handleUploadChange = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
    } else if (info.file.status === 'done' || info.file.status === 'error') {
      setLoading(false);
    }
  };

  const showConfirm = () => {
    confirm({
      title: 'Create a collaboration document?',
      icon: <ExclamationCircleFilled style={{ color: '#ff4500' }} />,
      content: 'Clicking OK will automatically log and sync document changes in real-time.',
      centered: true,
      okText: 'Confirm',
      okButtonProps: { style: { backgroundColor: '#ff4500', borderColor: '#ff4500' } },
      onOk() {
        dispatch(fetchAllUserData()).then((res) => {
          if (res?.payload?.data?.roomId) {
            history.push({
              pathname: '/new-document',
              state: { roomId: res.payload.data.roomId }
            });
          }
          dispatch(clearRoomUserData());
        });
      }
    });
  };

  const uploadPdfDoc = (file) => {
    const data = new FormData();
    data.append('pdfFile', file.file);
    dispatch(uploadPdfAsyn(data)).then((res) => {
      setLoading(false);
      if (res.code === 200) {
        message.success(res.message);
        setIsModalVisible(false);
        history.push({
          pathname: '/add-document',
          state: {
            documentId: res.data.documentId,
            processId: res.data.processId,
            createdAt: res.data.createdAt
          }
        });
      } else {
        message.error(res.message);
      }
    });
  };

  return (
    <div style={styles.container}>
      {(isLoading || isProcessFetching) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin indicator={antIcon} spinning={true} />
        </div>
      )}
      
      <Toaster position="top-right" />

      <Content>
        <div style={styles.welcomeSection}>
          <Row align="middle" justify="space-between">
            <Col>
  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
    Overview
  </Text>
  <Title level={2} style={{ color: '#fff', margin: '4px 0 0 0' }}>
    Welcome, {user.username.toUpperCase()}
  </Title>
  <Paragraph style={{ color: 'rgba(255,255,255,0.7)', margin: '8px 0 0 0' }}>
    Manage your processes and collaborative documents efficiently.
  </Paragraph>
</Col>
            <Col>
              <Button
                onClick={() => setIsModalVisible(true)}
                icon={<DoubleRightOutlined />}
                size="large"
                style={{ borderRadius: '8px', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
              >
                Create Process
              </Button>
            </Col>
          </Row>
        </div>

      {/* KPI Row - Three Columns: Total, Collaborations, and KIV */}
<Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
  {/* Total Processes Card */}
  <Col xs={24} sm={12} md={8}>
    <Card style={styles.kpiCard("#52c41a")}>
      <Statistic
        title={<Text type="secondary">Total Processes</Text>}
        value={processCount}
        valueStyle={{ fontWeight: '700' }}
        prefix={<FileTextOutlined style={{ color: "#52c41a", marginRight: 8 }} />}
      />
    </Card>
  </Col>

  {/* Collaborations Card */}
  <Col xs={24} sm={12} md={8}>
    <Card style={styles.kpiCard("#1677ff")}>
      <Statistic
        title={<Text type="secondary">Collaborations</Text>}
        value={collaborateCount}
        valueStyle={{ fontWeight: '700' }}
        prefix={<TeamOutlined style={{ color: "#1677ff", marginRight: 8 }} />}
      />
    </Card>
  </Col>

  {/* KIV Processes Card */}
  <Col xs={24} sm={24} md={8}>
    <Card style={styles.kpiCard("#fa8c16")}>
      <Statistic
        title={<Text type="secondary">KIV Processes</Text>}
        value={kivCount}
        valueStyle={{ fontWeight: '700' }}
        prefix={<CheckCircleOutlined style={{ color: "#fa8c16", marginRight: 8 }} />}
      />
    </Card>
  </Col>
</Row>

        <Card style={styles.mainCard} bodyStyle={{ padding: '32px' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
            <Title level={4} style={{ margin: 0 }}>Quick Actions</Title>
            <Space size="middle">
              <Button
                onClick={() => setIsModalVisible(true)}
                type="default"
                icon={<UploadOutlined />}
                style={{ ...styles.actionBtn, border: '1px solid #d9d9d9' }}
              >
                Upload Document
              </Button>
              <Button
                onClick={showConfirm}
                type="primary"
                icon={<FileAddOutlined />}
                style={{ ...styles.actionBtn, backgroundColor: '#ff4500', borderColor: '#ff4500' }}
              >
                New Collaborative Doc
              </Button>
            </Space>
          </Row>

          <Tabs defaultActiveKey="1" className="custom-tabs">
            <TabPane tab={<span><InboxOutlined />Instructions & Guide</span>} key="1">
              <div style={styles.instructionBox}>
                <Row gutter={24}>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ fontSize: 16, color: '#172B4D' }}>How to Upload?</Text>
                      <Paragraph style={{ marginTop: 8, color: '#6B778C' }}>
                        Click the "Upload Document" button. You can drag and drop your PDF file. 
                        Once uploaded, the system will categorize the document.
                      </Paragraph>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ fontSize: 16, color: '#172B4D' }}>Real-time Collaboration</Text>
                      <Paragraph style={{ marginTop: 8, color: '#6B778C' }}>
                        Selecting "New Document" starts a shared room. Any changes made are visible to 
                        all team members instantly.
                      </Paragraph>
                    </div>
                  </Col>
                </Row>
              </div>
            </TabPane>
          </Tabs>
        </Card>

        {/* Upload Modal */}
        <Modal
          centered
          width={550}
          title={<Text strong style={{ fontSize: 18 }}>Upload PDF Document</Text>}
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          bodyStyle={{ padding: '40px' }}
        >
          <Dragger
            accept=".pdf"
            showUploadList={false}
            customRequest={uploadPdfDoc}
            onChange={handleUploadChange}
            style={{ 
              padding: '40px', 
              borderRadius: '16px', 
              background: '#fafafa', 
              border: '2px dashed #d9d9d9' 
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: 48, color: "#ff4500" }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 18, fontWeight: 600, marginTop: 16 }}>
              Drop your PDF here
            </p>
            <p className="ant-upload-hint" style={{ color: "#888", padding: '0 20px' }}>
              Only .PDF format is accepted for automated processing.
            </p>
          </Dragger>
        </Modal>
      </Content>
    </div>
  );
}